import "./PollySurveyAuthoringForm.scss";

import {
  Alert,
  Button,
  Divider,
  ExclamationTriangleIcon,
  Flex,
  FlexItem,
  Segment,
  Skeleton,
  Text,
  Tooltip,
} from "@fluentui/react-northstar";
import { DialogDimension, dialog } from "@microsoft/teams-js";
import FormState, {
  FieldDescriptors,
  ValidatorDictionary,
  validateList,
} from "@shopify/react-form-state";
import { SurveyAuthoringPreviewCommand, SurveyLimits, getSurveyTypeDisplay } from "@teams/cards";
import {
  NominalQuestion,
  Question,
  QuestionType,
  TeamsRecipientMember,
  TeamsSurveyDraft,
  TeamsSurveyTypes,
} from "@teams/types";
import { SurveyUpdateOptionsDTO } from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useQuestionTemplates } from "../../../../_shared/actions/useQuestionTemplates";
import { useTeamsDetails } from "../../../../_shared/actions/useTeamsDetails";
import CrossIcon from "../../../../_shared/assets/cross-icon.svg";
import { TextInputField } from "../../../../_shared/components/fields";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { FetchError, isError } from "../../../../_shared/services/ChannelApi";
import { scheduleApi } from "../../../../_shared/services/ScheduleApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";
import { AudienceItem } from "../../../../_shared/types";
import { getAudienceFromTeamsContext } from "../../../../_shared/utils/Authoring";
import { getRRuleDTStart } from "../../../../_shared/utils/scheduleHelper";
import { getQuestionValidator, getSurveyTitleValidator } from "../../../../_shared/validators";
import { AuthoringFormHeader } from "../AuthoringFormHeader";
import { useTrackingProperties } from "../PollyAuthoring/hooks/useTrackingProperties";
import { PollyPreview } from "../PollyPreview";
import { TemplateSaveDialog, TemplateSaveFields } from "../Templates/TemplateSaveDialog";
import { AddAnotherQuestionButton } from "./AddAnotherQuestionButton";
import { EditingWarningAlert } from "./EditingWarningAlert";
import { PollySurveyAuthoringFormSettings } from "./PollySurveyAuthoringFormSettings";
import PreviewIcon from "./Preview.svg";
import { QuestionCardList } from "./QuestionCardList";
import { AuthoringFields, PollySurveyAuthoringProps } from "./types";
import useWindowDimensions from "./useWindowDimensions";

type PageActions = "CREATE_POLLY" | "UPDATE_POLLY" | "UPDATE_SCHEDULE" | "TEMPLATE";
const initialDialogWidth = 700;
const hidePreviewOnSize = 1000;
const initialDialogHeight = "large" as DialogDimension;

export const PollySurveyAuthoringForm: React.FC<PollySurveyAuthoringProps> = props => {
  const {
    defaultValues,
    showLoading,
    hideSaveAsTemplate,
    externalError,
    referenceTemplateId,
    allowManualSend,
    allowScheduledSend,
    isGlobalTemplate,
    messageId,
    editScheduleId,
    surveyId,
    compressed,
    useApiOnly,
    isMobile,
    onSaved,
    parentSurvey,
    isEdit,
    isLimitedEditForRecurring,
    duplicate,
    trackingFields,
    templateName,
    headerOptions,
  } = props;

  const { teamsContext } = useTeamsContext();
  const { inActiveMeeting, groupType, hostClientType, meetingType } = useTrackingProperties({
    isPersonal: trackingFields?.isPersonal ?? false,
  });
  const [showSaveTemplate, setShowSaveTemplate] = useState<boolean>();
  const saveMethod = useRef<"template" | "create" | "edit">("create");
  const { teamsDetails, isTeamsDetailsLoading, loadTeamsDetails } = useTeamsDetails();
  const [errors, setErrors] = useState<Error>();
  const [scrollToTitleIfEmpty, setScrollToTitleIfEmpty] = useState(false);
  const [showTitleRequiredPrompot, setShowTitleRequiredPrompt] = useState(false);
  const { questionTemplates, isQuestionTemplatesLoading, loadQuestionTemplates } =
    useQuestionTemplates();
  const [highlightLastQuestion, setHighlightLastQuestion] = useState(false);
  const [showNoRecipientToolTip, setShowNoRecipientToolTip] = useState(false);
  const [questionHighlightInterval, setQuestionHighlightInterval] = useState<NodeJS.Timeout>();
  const [collapsedQuestions, setCollapsedQuestions] = useState<
    { id: string; collapsed: boolean | undefined }[]
  >([]);

  const responsesCount = useMemo(() => parentSurvey?.responseCount ?? 0, [parentSurvey]);
  const isLimitedEdit = useMemo(
    () => (isEdit && parentSurvey?.state === "open") || isLimitedEditForRecurring,
    [isEdit, parentSurvey?.state, isLimitedEditForRecurring]
  );
  const questionValidator = useMemo(() => {
    return validateList(getQuestionValidator() as any);
  }, []);
  const ref = useRef<HTMLDivElement>(null);
  const isMeetingSidePanel = teamsContext.page.frameContext === "sidePanel";
  const { width } = useWindowDimensions();
  const [showPreviewForMobile, setShowPreviewForMobile] = useState(false);

  // resize the dialog to show preview
  useEffect(() => {
    if (isMobile) {
      return;
    }

    let error: boolean = false;

    try {
      dialog.update.resize({ width: 1400, height: "large" as DialogDimension });
    } catch (err) {
      error = true;
      console.warn("Unable to display previews");
    }

    return () => {
      if (error) {
        return;
      }

      if (isMobile) {
        return;
      }

      dialog.update.resize({ width: initialDialogWidth, height: initialDialogHeight });
    };
  }, [isMobile]);

  useEffect(() => {
    const questions = defaultValues.questions;
    let _collapsedQuestions: { id: string; collapsed: boolean | undefined }[];
    // If the polly contains only one question, keep it expanded by default in the edit view.
    // Otherwise collapse all questions by default.
    if (questions.length === 1) {
      _collapsedQuestions = [{ id: questions[0].id, collapsed: undefined }];
    } else {
      _collapsedQuestions = questions.map(question => {
        return { id: question.id, collapsed: true };
      });
    }
    setCollapsedQuestions(_collapsedQuestions);
  }, [defaultValues.questions]);

  useEffect(() => {
    loadQuestionTemplates();
    loadTeamsDetails(teamsContext, true);
  }, [teamsContext, loadTeamsDetails, loadQuestionTemplates]);

  useEffect(() => {
    if (!externalError) {
      return;
    }

    setErrors(new Error(externalError));
  }, [externalError]);

  const getCurrentAction = useCallback((): PageActions => {
    if (saveMethod.current === "template") {
      return "TEMPLATE";
    }

    if (saveMethod.current === "edit" && !editScheduleId) {
      return "UPDATE_POLLY";
    }

    if (!editScheduleId) {
      return "CREATE_POLLY";
    } else {
      return "UPDATE_SCHEDULE";
    }
  }, [editScheduleId]);

  const authoringFieldsToDraft = useCallback(
    (fields: FieldDescriptors<AuthoringFields>): TeamsSurveyDraft => {
      const draft: TeamsSurveyDraft = {
        type: fields.type.value,
        title: fields.type.value !== "poll" ? fields.title.value : fields.questions.value[0].label,
        deliveryPreference: fields.deliveryMethod.value || "in-group",
        description: fields.description.value,
        anonymity: fields.anonymity.value,
        resultsVisibility: fields.resultsVisibility.value,
        reminders: [],
        questions: fields.questions.value.map(questionFieldValue => {
          // exclude validationErrors before passing to server
          const { validationErrors, ...question } = questionFieldValue;
          return question;
        }),
        commentsEnabled: fields.commentsEnabled.value,
        commentsAppeal: fields.commentsAppeal.value,
      };

      if (fields.rrule.value) {
        draft.schedule = {
          rrule: fields.rrule.value,
          rruleDTStart: getRRuleDTStart(fields.sendAt.value!),
          rruleTZID: Intl.DateTimeFormat().resolvedOptions().timeZone,
          openDuration: fields.openDuration.value as string,
        };
      } else {
        if (fields.sendAt.value) {
          draft.deliverAt = moment(fields.sendAt.value).isAfter()
            ? fields.sendAt.value
            : new Date();
        }

        if (fields.openDuration.value) {
          draft.openDuration = fields.openDuration.value;
        }
      }

      return draft;
    },
    []
  );

  const getAudienceForPolly = useCallback(() => {
    const audience = getAudienceFromTeamsContext(teamsContext) as AudienceItem;

    if (!audience.messageId && messageId) {
      audience.messageId = messageId;
    }

    return [audience];
  }, [messageId, teamsContext]);

  const createDraft = useCallback(
    (fields: FieldDescriptors<AuthoringFields>, action?: PageActions): TeamsSurveyDraft => {
      if (action === "CREATE_POLLY") {
        return {
          ...authoringFieldsToDraft(fields),
          templateId: referenceTemplateId,
          recipients: getAudienceForPolly(),
          ...(duplicate && {
            duplicateFromSurveyId: surveyId,
          }),
        };
      } else if (action === "UPDATE_POLLY") {
        let recipients: TeamsRecipientMember[] | undefined;

        if (isLimitedEdit) {
          recipients = parentSurvey?.recipients;
        } else {
          recipients = getAudienceForPolly();

          // In full edit, check if a web recipient exists for the survey (i.e a web share link was generated for the survey)
          const webRecipient = parentSurvey?.recipients.find(recp => recp.type === "web");
          if (webRecipient) {
            recipients?.push(webRecipient);
          }
        }

        return {
          ...authoringFieldsToDraft(fields),
          templateId: referenceTemplateId,
          recipients: recipients,
        };
      } else {
        return authoringFieldsToDraft(fields);
      }
    },
    [
      authoringFieldsToDraft,
      duplicate,
      getAudienceForPolly,
      isLimitedEdit,
      parentSurvey?.recipients,
      referenceTemplateId,
      surveyId,
    ]
  );

  const setCollapsedForQuestion = useCallback(
    (questionId: string, collapsed: boolean, fields: FieldDescriptors<AuthoringFields>) => {
      const validationResult = questionValidator(
        fields.questions.value.filter(x => x.id === questionId),
        fields
      ) as { [key: string]: any }[];

      // Prevent collapsing question if question is invalid and set errors on the validationErrors object
      if (validationResult) {
        const firstValidationResult = validationResult[0];
        fields.questions.onChange(
          fields.questions.value.map(q => {
            if (q.id === questionId) {
              q.validationErrors = {
                labelError: firstValidationResult["label"],
                choicesError: firstValidationResult["choices"],
              };
            }
            return q;
          })
        );
        return;
      }
      // If validation passes, remove any error messages previously set
      fields.questions.onChange(
        fields.questions.value.map(q => {
          if (q.id === questionId) {
            delete q.validationErrors;
          }
          return q;
        })
      );
      let updated = collapsedQuestions?.map(val => {
        return val.id === questionId ? { id: val.id, collapsed } : val;
      });
      setCollapsedQuestions(updated);
    },
    [collapsedQuestions, questionValidator]
  );

  const expandAllQuestions = useCallback(() => {
    let updated = collapsedQuestions?.map(val => {
      return { ...val, collapsed: false };
    });
    setCollapsedQuestions(updated);
  }, [collapsedQuestions]);

  const createPolly = useCallback(
    async (draft: Readonly<TeamsSurveyDraft>) => {
      if (teamsDetails?.active) {
        //Try saving against the api first
        const result = await surveyApi.createSurvey(teamsContext, draft, {
          authoringOrigin: trackingFields?.authoringOrigin,
          trackingSource: trackingFields?.trackingSource,
          inActiveMeeting,
          groupType,
          hostClientType,
          meetingType,
        });

        if (!isError(result)) {
          if (result.survey?.id) {
            onSaved(false, result.survey?.id);
          } else if (result.scheduleId) {
            onSaved(false, undefined, false, true);
          }
          return;
        } else if (useApiOnly) {
          setErrors(result);
          return;
        }
      }

      if (!useApiOnly) {
        //May not be installed to this team/chat yet so do preview stage
        const command = SurveyAuthoringPreviewCommand(draft, {
          authoringOrigin: trackingFields?.authoringOrigin,
          trackingSource: trackingFields?.trackingSource,
          groupType,
          inActiveMeeting,
          hostClientType,
          meetingType,
        }).toCommand();

        return new Promise(() => {
          dialog.url.submit(command);
          onSaved(true);
        });
      }
    },
    [
      teamsDetails?.active,
      useApiOnly,
      teamsContext,
      trackingFields?.authoringOrigin,
      trackingFields?.trackingSource,
      inActiveMeeting,
      groupType,
      hostClientType,
      meetingType,
      onSaved,
    ]
  );

  const updatePolly = useCallback(
    async (
      draft: Readonly<TeamsSurveyDraft>,
      updateOptions: Pick<SurveyUpdateOptionsDTO, "notifyUsersOnEdit" | "removeExistingVotesOnEdit">
    ) => {
      const result = await surveyApi.updateSurvey(teamsContext, surveyId!, draft, updateOptions);

      if (!isError(result)) {
        onSaved(false, result.survey?.id, true);
      } else {
        const message =
          result instanceof FetchError ? (result.body?.message as string) : result.message;

        return new Error(message);
      }
    },
    [onSaved, surveyId, teamsContext]
  );

  const scrollToTitleIfRequired = useCallback(
    (surveyType: TeamsSurveyTypes, surveyTitle: string) => {
      const isScrollRequired =
        scrollToTitleIfEmpty && surveyType === "survey" && surveyTitle.trim().length === 0;

      if (!isScrollRequired) {
        return isScrollRequired;
      }

      setScrollToTitleIfEmpty(false);
      setShowTitleRequiredPrompt(true);
      ref.current?.scrollIntoView({ behavior: "smooth" });

      return isScrollRequired;
    },
    [scrollToTitleIfEmpty]
  );

  const addQuestion = useCallback(
    (
      fields: FieldDescriptors<Pick<AuthoringFields, "questions" | "type">>,
      question: Question<QuestionType>
    ) => {
      const lastQuestion = fields.questions.value[fields.questions.value.length - 1];
      fields.questions.onChange([...fields.questions.value, question]);

      setHighlightLastQuestion(true);

      const interval = setTimeout(() => {
        setHighlightLastQuestion(false);
      }, 3000);

      if (questionHighlightInterval) clearInterval(questionHighlightInterval);

      setQuestionHighlightInterval(interval);

      if (fields.type.value === "poll") {
        fields.type.onChange("survey");

        if (!scrollToTitleIfEmpty) {
          setScrollToTitleIfEmpty(true);
        }
      }

      // Collapse the last question on adding a new question
      const validationResult = questionValidator([lastQuestion], fields);
      let updated = collapsedQuestions.map((val, index) => {
        // Collapse previous question only if it passes validation
        if (index === collapsedQuestions.length - 1 && !validationResult) {
          return val.collapsed === undefined ? { id: val.id, collapsed: true } : val;
        }
        return val;
      });
      setCollapsedQuestions([...updated, { id: question.id, collapsed: undefined }]);
    },
    [collapsedQuestions, questionHighlightInterval, questionValidator, scrollToTitleIfEmpty]
  );

  const formSubmitHandler = useCallback(
    async ({ fields }: { fields: FieldDescriptors<AuthoringFields> }) => {
      setErrors(undefined);

      const currentAction = getCurrentAction();
      let result: unknown | Error;

      if (currentAction === "TEMPLATE") {
        setShowSaveTemplate(true);
      } else if (currentAction === "UPDATE_POLLY") {
        const draft = createDraft(fields, currentAction);
        // Call the update API
        result = await updatePolly(draft, {
          notifyUsersOnEdit: fields.notifyUsersOnEdit.value,
          removeExistingVotesOnEdit: fields.removeExistingVotesOnEdit.value,
        });

        if (isError(result)) {
          return [result];
        }
      } else if (currentAction === "CREATE_POLLY") {
        const draft = createDraft(fields, currentAction);
        result = await createPolly(draft);

        if (isError(result)) {
          return [result];
        }
      } else if (currentAction === "UPDATE_SCHEDULE") {
        const draft = createDraft(fields, currentAction);
        result = await scheduleApi.update(teamsContext, editScheduleId!, draft, {
          limitedEdit: isLimitedEdit,
          notifyUsersOnEdit: fields.notifyUsersOnEdit.value,
          removeExistingVotesOnEdit: fields.removeExistingVotesOnEdit.value,
        });

        if (isError(result)) {
          const message =
            result instanceof FetchError ? (result.body?.message as string) : result.message;

          return [new Error(message)];
        } else {
          onSaved();
        }
      }
    },
    [
      createDraft,
      createPolly,
      editScheduleId,
      getCurrentAction,
      isLimitedEdit,
      onSaved,
      teamsContext,
      updatePolly,
    ]
  );

  if (
    isTeamsDetailsLoading !== false ||
    isQuestionTemplatesLoading !== false ||
    showLoading !== false
  ) {
    return (
      <Skeleton animation="wave">
        <Flex column gap="gap.medium">
          <Skeleton.Shape width="100%" height="250px" />
          <Skeleton.Line height="2px" />
          <Skeleton.Button width="100%" />
          <Skeleton.Line width="50%" />
          <Skeleton.Line width="50%" />
          <Skeleton.Line width="50%" />
          <Skeleton.Line height="2px" />
          <Skeleton.Button />
        </Flex>
      </Skeleton>
    );
  }

  return (
    <FormState
      initialValues={defaultValues}
      validateOnSubmit
      externalErrors={errors ? [errors] : undefined}
      onSubmit={formSubmitHandler}
      validators={
        {
          title: getSurveyTitleValidator("Title") as any,
          questions: validateList(getQuestionValidator() as any),
        } as Partial<ValidatorDictionary<AuthoringFields>>
      }
    >
      {({ fields, submitting, submit, errors }) => {
        // TODO: remove this and return the JSX only
        if (fields.sendAt && isLimitedEdit) {
          fields.sendAt.onChange(undefined);
        }

        return (
          <Flex
            gap="gap.medium"
            className={`survey-authoring-form ${showPreviewForMobile ? "preview" : ""}`}
          >
            {((!isMobile && width > hidePreviewOnSize) || !showPreviewForMobile) && (
              <FlexItem className="form-container">
                <div>
                  {headerOptions?.showHeader && (
                    <AuthoringFormHeader
                      isEdit={headerOptions.isEdit}
                      templateId={headerOptions.templateId}
                      trackingSource={headerOptions.trackingSource}
                    />
                  )}

                  <Flex column gap="gap.small">
                    {templateName && (
                      <Text content={templateName} size="medium" align="center" weight="bold" />
                    )}

                    {isEdit && (
                      <EditingWarningAlert
                        isLimitedEdit={isLimitedEdit ?? false}
                        responsesCount={responsesCount}
                      />
                    )}

                    <Flex column gap="gap.small">
                      {fields.type.value !== "poll" && (
                        <Segment>
                          <div ref={ref}>
                            <Flex column gap="gap.small">
                              <TextInputField
                                field={fields.title}
                                label="Title"
                                labelProps={{ weight: "regular" }}
                                maxLength={SurveyLimits.Title.MaxLength}
                                placeholder="Enter the title of your polly"
                                fluid
                              />
                              {showTitleRequiredPrompot && (
                                <Text color="brand">
                                  Please add a title for your multi-question polly
                                </Text>
                              )}
                              <TextInputField
                                field={fields.description}
                                label="Description (optional)"
                                fluid
                                labelProps={{ weight: "regular" }}
                                placeholder="Enter a description of your polly"
                                maxLength={SurveyLimits.Description.MaxLength}
                              />
                            </Flex>
                          </div>
                        </Segment>
                      )}

                      <QuestionCardList
                        fields={fields}
                        questionTemplates={questionTemplates}
                        compressed={compressed}
                        isEdit={isEdit}
                        isLimitedEdit={isLimitedEdit}
                        highlightLastQuestion={highlightLastQuestion}
                        addQuestion={addQuestion}
                        collapsedQuestions={collapsedQuestions}
                        setCollapsedForQuestion={setCollapsedForQuestion}
                        expandAllQuestions={expandAllQuestions}
                        isMeetingSidePanel={isMeetingSidePanel}
                      />

                      {!isLimitedEdit && (
                        <AddAnotherQuestionButton fields={fields} addQuestion={addQuestion} />
                      )}

                      {fields.questions.value.length > 1 &&
                        fields.questions.value[0].questionType === "nominal" &&
                        (fields.questions.value[0] as NominalQuestion).allowUserAddOptions && (
                          <Alert
                            warning
                            content="One of your questions allowed for the audience to add new choices. This is not supported in multi-question Polly's. The question will still be sent but users will not be able to add options."
                          />
                        )}

                      <Divider
                        size={1}
                        content={`${getSurveyTypeDisplay(fields.type.value, {
                          capitalize: true,
                        })} Settings`}
                      />

                      <PollySurveyAuthoringFormSettings
                        fields={fields}
                        parentSurvey={parentSurvey}
                        editScheduleId={editScheduleId}
                        referenceTemplateId={referenceTemplateId}
                        allowManualSend={allowManualSend}
                        allowScheduledSend={allowScheduledSend}
                        compressed={compressed}
                        isLimitedEdit={isLimitedEdit}
                        isMobile={isMobile}
                        isEdit={isEdit}
                        teamsDetails={teamsDetails}
                      />

                      <Divider size={1} />

                      {errors && (
                        <Flex column>
                          {errors.map(error => (
                            <Alert
                              danger
                              icon={<ExclamationTriangleIcon />}
                              content={error.message}
                              attached="top"
                            />
                          ))}
                        </Flex>
                      )}

                      <Flex hAlign="end">
                        <Flex gap="gap.small">
                          {!isEdit && !hideSaveAsTemplate && (
                            <Button
                              secondary
                              disabled={submitting}
                              content={
                                referenceTemplateId && !isGlobalTemplate
                                  ? "Save template changes"
                                  : "Save as template"
                              }
                              icon={<PollyIcon icon="save-icon" inheritColor />}
                              onClick={() => {
                                saveMethod.current = "template";
                                if (
                                  scrollToTitleIfRequired(fields.type.value, fields.title.value)
                                ) {
                                  return;
                                } else {
                                  setShowTitleRequiredPrompt(false);
                                }
                                submit();
                              }}
                            />
                          )}
                          {isEdit && (
                            <Button
                              secondary
                              content={"Cancel"}
                              onClick={() => {
                                dialog.url.submit();
                              }}
                            />
                          )}
                          <Tooltip
                            open={showNoRecipientToolTip}
                            content={"You must select a channel before sending this polly"}
                          >
                            <Button
                              primary
                              content={
                                !!isEdit
                                  ? "Update Polly"
                                  : fields.rrule.value || moment(fields.sendAt.value).isAfter()
                                  ? "Save and schedule"
                                  : fields.sendAt.value
                                  ? `Send now ${
                                      fields.deliveryMethod.value === "direct-message"
                                        ? " by DM"
                                        : ""
                                    }`
                                  : "Save"
                              }
                              icon={
                                isEdit || fields.rrule.value || !fields.sendAt.value ? undefined : (
                                  <PollyIcon icon="send-icon" inheritColor />
                                )
                              }
                              onClick={() => {
                                saveMethod.current = isEdit ? "edit" : "create";
                                if (
                                  scrollToTitleIfRequired(fields.type.value, fields.title.value)
                                ) {
                                  return;
                                } else {
                                  setShowTitleRequiredPrompt(false);
                                }
                                submit();
                              }}
                              disabledFocusable={submitting}
                              onMouseEnter={() => {
                                setShowNoRecipientToolTip(false);
                              }}
                              onMouseLeave={() => {
                                setShowNoRecipientToolTip(false);
                              }}
                              loading={submitting}
                            />
                          </Tooltip>
                        </Flex>
                      </Flex>

                      {showSaveTemplate && (
                        <TemplateSaveDialog
                          open
                          header={
                            referenceTemplateId && !isGlobalTemplate
                              ? "Update Template"
                              : "Save as template"
                          }
                          updateTemlateId={referenceTemplateId}
                          isGlobalTemplate={isGlobalTemplate}
                          onSave={() => {
                            const templateFields: TemplateSaveFields = {
                              title: fields.title.value || fields.questions.value[0].label,
                              description: "",
                              questions: fields.questions.value,
                              commentsEnabled: fields.commentsEnabled.value,
                              commentsAppeal: fields.commentsAppeal.value,
                              resultsVisibility: fields.resultsVisibility.value,
                              anonymity: fields.anonymity.value,
                              deliveryPreference: "in-group",
                              type: fields.type.value,
                              reminders: [],
                            };

                            if (fields.openDuration.value) {
                              templateFields.openDuration = fields.openDuration.value;
                            }

                            return templateFields;
                          }}
                          onClose={() => setShowSaveTemplate(false)}
                        />
                      )}
                    </Flex>
                  </Flex>
                </div>
              </FlexItem>
            )}

            <>
              {!isMobile && width > hidePreviewOnSize && (
                <FlexItem className="preview-container">
                  <div>
                    <Flex column className="preview">
                      <FlexItem styles={{ padding: "20px 0" }}>
                        <Divider size={1} content={`Preview`} />
                      </FlexItem>
                      <FlexItem>
                        <div>
                          <PollyPreview survey={createDraft(fields, getCurrentAction())} />
                        </div>
                      </FlexItem>
                    </Flex>
                  </div>
                </FlexItem>
              )}

              {(isMobile || (!isMobile && width <= hidePreviewOnSize)) && (
                <>
                  {showPreviewForMobile && (
                    <Flex
                      hAlign="center"
                      vAlign="center"
                      gap="gap.small"
                      className={`preview-label`}
                    >
                      <img src={PreviewIcon} alt="" />
                      <Text weight="bold" styles={{ marginBottom: "2px" }}>
                        Preview
                      </Text>
                    </Flex>
                  )}
                  {/* Only show preview icon once question input has begun */}
                  {!(
                    fields.questions.value.length === 1 &&
                    !fields.questions.value[fields.questions.value.length - 1].label
                  ) && (
                    <Flex
                      hAlign="center"
                      vAlign="center"
                      className={`preview-popup-icon ${isMobile ? "mobile" : ""} ${
                        isMeetingSidePanel ? "side-panel" : ""
                      }`}
                      onClick={() => {
                        setShowPreviewForMobile(!showPreviewForMobile);
                      }}
                    >
                      <img src={showPreviewForMobile ? CrossIcon : PreviewIcon} alt="" />
                    </Flex>
                  )}

                  {showPreviewForMobile && (
                    <PollyPreview survey={createDraft(fields, getCurrentAction())} />
                  )}
                </>
              )}
            </>
          </Flex>
        );
      }}
    </FormState>
  );
};
